import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, ExternalLink, AlertTriangle } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { AGRITOURISM, CATEGORY_TABLE, FEATURED_EXAMPLE, RURAL_REVIEW_DATE } from "@/data/rural";

const RuralAgritourism = () => (
  <div data-testid="rural-agritourism-page">
    <Seo
      title="Understanding Agritourism Without the Planning Jargon"
      description="The NSW agritourism reforms explained plainly: farm stay, farm gate and farm experience premises, the commercial farm test, and the exempt, complying and DA pathways."
    />
    <section className="bg-sand-light/40">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
        <Link to="/rural" data-testid="agritourism-back" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-charcoal/60 hover:text-teal transition-colors">
          <ArrowLeft className="w-4 h-4" /> Rural Land
        </Link>
        <FadeIn>
          <h1 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-teal tracking-tight">{AGRITOURISM.title}</h1>
          <p className="mt-4 text-base md:text-lg text-charcoal/75 leading-relaxed">{AGRITOURISM.lead}</p>
          <p className="mt-4 text-sm text-charcoal/70 leading-relaxed max-w-3xl">
            One distinction matters above all: owning rural land does not automatically permit
            camping, tourist accommodation or commercial activities. The reforms create pathways
            for qualifying commercial farms — vacant bush blocks and hobby farms may need a
            different route entirely.
          </p>
        </FadeIn>
      </div>
    </section>

    <section className="py-12 lg:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
        <FadeIn>
          <div>
            <Eyebrow>The three categories</Eyebrow>
            <div className="mt-5 border border-cloud bg-white overflow-x-auto">
              <table className="w-full text-sm" data-testid="category-table">
                <thead>
                  <tr className="bg-teal text-ivory text-left">
                    <th className="px-5 py-3.5 font-medium">Planning term</th>
                    <th className="px-5 py-3.5 font-medium">Potential income opportunity</th>
                  </tr>
                </thead>
                <tbody>
                  {CATEGORY_TABLE.map((r) => (
                    <tr key={r.term} className="border-t border-cloud">
                      <td className="px-5 py-4 font-medium text-teal whitespace-nowrap">{r.term}</td>
                      <td className="px-5 py-4 text-charcoal/80 leading-relaxed">{r.opportunity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 space-y-4">
              {AGRITOURISM.definitions.map((d) => (
                <div key={d.term} className="border-l-2 border-sand pl-5">
                  <p className="font-medium text-charcoal">{d.term}</p>
                  <p className="mt-1 text-sm text-charcoal/75 leading-relaxed">{d.def}</p>
                </div>
              ))}
            </div>
          </div>
        </FadeIn>

        <FadeIn>
          <div>
            <Eyebrow>The commercial farm test</Eyebrow>
            <div className="mt-4 space-y-4">
              {AGRITOURISM.commercialFarm.map((p, i) => (
                <p key={i} className="text-charcoal/80 leading-relaxed">{p}</p>
              ))}
            </div>
          </div>
        </FadeIn>

        <FadeIn>
          <div className="bg-white border-2 border-teal p-7" data-testid="featured-example">
            <Eyebrow>{FEATURED_EXAMPLE.title}</Eyebrow>
            <p className="mt-3 text-charcoal/80 leading-relaxed">{FEATURED_EXAMPLE.intro}</p>
            <ul className="mt-4 space-y-2.5">
              {FEATURED_EXAMPLE.standards.map((s, i) => (
                <li key={i} className="flex gap-3 text-sm text-charcoal/85 leading-relaxed">
                  <span className="text-teal font-mono shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  {s}
                </li>
              ))}
            </ul>
            <p className="mt-5 flex gap-2.5 text-sm text-charcoal/80 leading-relaxed bg-sand-light/60 p-4">
              <AlertTriangle className="w-4 h-4 text-teal shrink-0 mt-0.5" />
              {FEATURED_EXAMPLE.caution}
            </p>
            <a
              href={FEATURED_EXAMPLE.source.url}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.14em] text-teal underline underline-offset-4"
            >
              {FEATURED_EXAMPLE.source.label} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </FadeIn>

        <FadeIn>
          <div>
            <Eyebrow>The planning framework</Eyebrow>
            <div className="mt-5 space-y-5">
              {AGRITOURISM.framework.map((f) => (
                <div key={f.name} className="bg-white border border-cloud p-5">
                  <p className="font-medium text-teal">{f.name}</p>
                  <p className="mt-1.5 text-sm text-charcoal/75 leading-relaxed">{f.plain}</p>
                </div>
              ))}
            </div>
          </div>
        </FadeIn>

        <FadeIn>
          <div>
            <Eyebrow>The three principal pathways</Eyebrow>
            <div className="mt-5 grid sm:grid-cols-3 gap-4" data-testid="pathways-grid">
              {AGRITOURISM.pathways.map((p, i) => (
                <div key={p.name} className="bg-white border border-cloud p-5">
                  <span className="text-xs font-mono text-sand">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-2 font-serif text-lg text-teal">{p.name}</p>
                  <p className="mt-2 text-sm text-charcoal/75 leading-relaxed">{p.plain}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-charcoal/70 leading-relaxed">
              Even where planning consent is not required, another approval, permit, licence or
              certification may still be necessary.
            </p>
          </div>
        </FadeIn>

        <FadeIn>
          <div>
            <Eyebrow>{AGRITOURISM.comparison.title}</Eyebrow>
            <div className="mt-5 border border-cloud bg-white overflow-x-auto">
              <table className="w-full text-sm" data-testid="comparison-table">
                <thead>
                  <tr className="bg-teal text-ivory text-left">
                    <th className="px-5 py-3.5 font-medium">The idea</th>
                    <th className="px-5 py-3.5 font-medium">What to check</th>
                    <th className="px-5 py-3.5 font-medium">Likely pathway</th>
                  </tr>
                </thead>
                <tbody>
                  {AGRITOURISM.comparison.rows.map((r) => (
                    <tr key={r.idea} className="border-t border-cloud align-top">
                      <td className="px-5 py-4 text-charcoal/85 leading-relaxed">{r.idea}</td>
                      <td className="px-5 py-4 text-charcoal/70 leading-relaxed">{r.check}</td>
                      <td className="px-5 py-4 text-charcoal/85 leading-relaxed">{r.approval}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-charcoal/60 leading-relaxed">{AGRITOURISM.comparison.note}</p>
            <p className="mt-2 text-xs text-charcoal/55">Legislative content last reviewed {RURAL_REVIEW_DATE}.</p>
          </div>
        </FadeIn>

        <FadeIn>
          <div className="flex flex-wrap gap-3">
            <Link to="/rural/explorer" data-testid="agritourism-cta-explorer" className="inline-flex items-center gap-2 bg-teal text-ivory text-sm font-medium px-6 py-3 rounded-full hover:bg-teal-dark transition-colors">
              Explore my land's potential <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/book" className="inline-flex items-center gap-2 border border-teal text-teal text-sm font-medium px-6 py-3 rounded-full hover:bg-teal hover:text-ivory transition-colors">
              Speak to a Planner <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  </div>
);

export default RuralAgritourism;
